import * as util from "lib/util"
import * as opt from "lib/opt"
import * as result from "lib/result"

String.prototype.Repeat = function (this: string, count: int): string {
	if (count < 0) {
		log.Fatal("count must be non-negative")
	}

	if (count === 0 || this.length === 0) {
		return ""
	}

	return this.repeat!(count)
}

String.prototype.String = function (this: string): string {
	return this.toString()
}

String.prototype.ParseBool = function (this: string): Result<bool> {
	// convert string literal true or false to boolean
	const strLower = this.ToLower()
	if (strLower !== "true" && strLower !== "false") {
		return result.Err(new Error(fmt.Sprintf("failed to parse %s to bool", this)))
	}
	return result.Ok(strLower === "true" ? true : false)
}

String.prototype.PadStart = String.prototype.padStart!

// removes all leading white space
String.prototype.TrimLeft = function (this: string): string {
	return this.replace!(/^\s+/g, "")
}
// removes all trailing white space
String.prototype.TrimRight = function (this: string): string {
	return this.replace!(/\s+$/g, "")
}

String.prototype.Title = function (this: string): string {
	return util.ProperCase(this)
}

String.prototype.TrimSpace = String.prototype.trim!

String.prototype.Contains = String.prototype.includes!

String.prototype.Len = function (this: string): int {
	return this.length
}

String.prototype.IsEmpty = function (this: string): bool {
	return util.IsEmpty(this)
}

String.prototype.Split = String.prototype.split!

String.prototype.Lines = function (this: string): string[] {
	return this.split!(/\r?\n/)
}

String.prototype.ToUpper = String.prototype.toUpperCase!
String.prototype.ToLower = String.prototype.toLowerCase!

String.prototype.Equal = function <T extends string>(other: T): bool {
	return this === other
}

String.prototype.Match = function <S extends string, R>(
	this: S,
	handlers: StringMatchHandlers<S, R>
): R {
	// Check if we have a direct handler
	if (this in handlers) {
		return (handlers as ExhaustiveHandlers<S, R>)[this as S](this)
	}

	// Check if we have a wildcard handler
	const wildcardHandler =
		"_" in handlers ? (handlers as PartialHandlersWithWildcard<S, R>)._ : undefined

	if (wildcardHandler) {
		return wildcardHandler(this)
	}

	/* eslint-disable */
	//@ts-ignore
	const unhandledKeys = Object.keys(handlers)
		.filter((k) => k !== "_")
		.join(", ")

	log.Fatalf("Unhandled string value: %s. Expected one of: %s", this, unhandledKeys)
}

String.prototype.FindAllString = function (this: string, pattern: RegExp): Array<string> {
	return Array.from(this.matchAll!(pattern)).Map((m) => m[0])
}

String.prototype.Parse = function <T>(this: string): Result<T> {
	if ((typeof {} as T) === "string") {
		return result.Ok(this as T)
	}

	if ((typeof {} as T) === "number") {
		const num = +this
		if (!isNaN(num)) {
			return result.Ok(num as T)
		}
		return result.Err(new Error("Failed to parse int"))
	}

	if ((typeof {} as T) === "boolean") {
		const lower = this.toLowerCase!()
		if (lower === "true") return result.Ok(true as T)
		if (lower === "false") return result.Ok(false as T)
		return result.Err(new Error("Failed to parse bool"))
	}

	// Fallback to JSON parsing for other types
	try {
		return result.Ok(JSON.parse(this) as T)
	} catch {
		return result.Err(new Error("Failed to parse JSON"))
	}
}

String.prototype.HasPrefix = String.prototype.startsWith!

String.prototype.Replace = function (
	this: string,
	oldOrPattern: string | RegExp,
	newOrFunc: string | ((substring: string, ...args: any[]) => string),
	n: int = -1
): string {
	// Case 1: When oldOrPattern is a RegExp and newOrFunc is a function (JS style)
	if (oldOrPattern instanceof RegExp && typeof newOrFunc === "function") {
		return this.replace!(oldOrPattern, newOrFunc as any)
	}

	// Case 2: When both parameters are strings (Go style)
	if (typeof oldOrPattern === "string" && typeof newOrFunc === "string") {
		const old = oldOrPattern
		const new_ = newOrFunc

		if (n === 0) {
			return this
		}

		if (n < 0) {
			// replace! all occurrences
			return this.replace!(new RegExp(old.replace!(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), new_)
		}

		// replace! up to n occurrences
		let count = 0
		return this.replace!(
			new RegExp(old.replace!(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
			(match) => {
				if (count < n) {
					count++
					return new_
				}
				return match
			}
		)
	}

	// Case 3: When oldOrPattern is a string/RegExp and newOrFunc is a function (hybrid)
	if (
		(typeof oldOrPattern === "string" || oldOrPattern instanceof RegExp) &&
		typeof newOrFunc === "function"
	) {
		// If it's a string, convert to RegExp
		const pattern =
			typeof oldOrPattern === "string"
				? new RegExp(oldOrPattern.replace!(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")
				: oldOrPattern

		if (n === 0) {
			return this
		}

		if (n < 0 || pattern.global) {
			// If n < 0 or pattern is already global, use as is
			return this.replace!(pattern, newOrFunc as any)
		}

		// If n > 0 and pattern is not global, limit replace!ments
		let count = 0
		return this.replace!(
			new RegExp(pattern.source, pattern.flags + (pattern.global ? "" : "g")),
			(...args) => {
				if (count < n) {
					count++
					return (newOrFunc as Function)(...args)
				}
				return args[0] // Return the original match
			}
		)
	}

	// Fallback for unhandled cases
	return this.replace!(oldOrPattern as any, newOrFunc as any)
}

String.prototype.HasPrefix = function (this: string, prefix: string): bool {
	return this.startsWith!(prefix)
}

String.prototype.HasSuffix = function (this: string, suffix: string): bool {
	return this.endsWith(suffix)
}

String.prototype.LastIndex = function (this: string, substr: string): Opt<int> {
	const index = this.lastIndexOf(substr)
	return index >= 0 ? opt.Some(index) : opt.None<int>()
}

String.prototype.Map = function (this: string, mapping: (r: string) => string): string {
	let result = ""
	for (let i = 0; i < this.length; i++) {
		result += mapping(this[i])
	}
	return result
}

//"  hello   world  ".Fields() // Returns ["hello", "world"]
String.prototype.Fields = function (this: string): string[] {
	return this.trim!().split!(/\s+/)
}

String.prototype.Take = function <T>(this: string, n: int): string {
	return this.slice(0, n)
}

String.prototype.Skip = function <T>(this: string, n: int): string {
	return this.slice(n)
}
